import { useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, type IntlShape } from 'react-intl';

import { WORKFLOW_CONTEXT_USER_REQUEST_MARKER } from '../../../contracts';
import type { Workflow } from '../../../contracts/routines.types';
import {
  ownedTriggerSentence,
  type buildTriggerLine,
} from '../../../contracts/workflows/lib/build-trigger-line';
import { isPresetCatalogWorkflow } from '../../routines/workflow-from-preset';

type TriggerLine = NonNullable<ReturnType<typeof buildTriggerLine>>;

export interface BuildIntroOptions {
  /** Real schedule / event line for owned routines (from API, not suggested_*). */
  triggerLine?: TriggerLine | null;
  /** Owned routine that has never run — Heidi asks before starting one. */
  neverRun?: boolean;
}

/** The hero card settles in before the overview starts revealing. */
const HERO_DELAY_MS = 300;
/** Pause between paragraph blocks — short enough that the per-block fades
 * overlap into one continuous reveal rather than discrete, staggered beats. */
const BLOCK_INTERVAL_MS = 220;

const INTRO_MESSAGES = defineMessages({
  cadenceDaily: {
    defaultMessage:
      'Out of the box it runs every morning, so the result is waiting before your day starts.',
    id: 'KM7rCrxjHu',
    description: 'Routine intro chat: cadence line for a daily routine',
  },
  cadenceWeekly: {
    defaultMessage:
      'Out of the box it runs once a week, wrapping the week up for you automatically.',
    id: 'JWN7tspqPO',
    description: 'Routine intro chat: cadence line for a weekly routine',
  },
  cadenceAfterNote: {
    defaultMessage:
      'It triggers automatically after each note, so there is nothing to remember. It just happens as you work.',
    id: 'QrzUKsuteR',
    description: 'Routine intro chat: cadence line for an event-triggered routine',
  },
  cadenceOnDemand: {
    defaultMessage:
      'It runs whenever you ask. Nothing happens in the background until you say so.',
    id: 'nRIPixUOB0',
    description: 'Routine intro chat: cadence line for an on-demand routine',
  },
  openEndedGreeting: {
    defaultMessage: '{name} is a flexible one. We can take it a few ways.',
    id: 'S+cGb9mImN',
    description:
      'Routine intro chat: greeting for an open-ended routine, {name} is the routine name',
  },
  greeting: {
    defaultMessage: "Here's what {name} does.",
    id: 'NDNjjfd+F8',
    description:
      'Routine intro chat: greeting for a well-defined routine, {name} is the routine name',
  },
  eachRunLeadIn: {
    defaultMessage: 'Each time it runs, it will:',
    id: 'TIoN8y4/g7',
    description: 'Routine intro chat: lead-in line above the highlight bullet list',
  },
  alreadyYours: {
    defaultMessage:
      "It's already yours. Run routine, use Edit for schedule and triggers, or just tell me what you'd like to change.",
    id: 'Yi5tExT93B',
    description: 'Routine intro chat: closing line for an owned open-ended routine',
  },
  alreadyYoursOwned: {
    defaultMessage:
      "It's already yours. Run routine whenever you want, use Edit for schedule and triggers, or just tell me what you'd like to change.",
    id: 'znPZEGq+3U',
    description: 'Routine intro chat: closing line for an owned well-defined routine',
  },
  neverRun: {
    defaultMessage: "{name} hasn't run yet. Would you like me to run it now?",
    id: 'DjMB0ZpA51',
    description:
      'Routine intro chat: closing line when an owned routine has never run, {name} is the routine name',
  },
  makeItYoursWithSetup: {
    defaultMessage:
      "Want to make it yours? Add routine and I'll walk you through the setup questions for your practice: when it should run, how it should work in your environment, and what to call it. You can also just tell me what you'd like to change.",
    id: 'fw0ExFRggP',
    description:
      'Routine intro chat: closing line for a system preset with setup questions',
  },
  makeItYours: {
    defaultMessage:
      "Want to make it yours? Try routine to see it in action once, or Add routine and I'll ask when it should run, what it should do, and what to call it. You can also just tell me what you'd like to change.",
    id: 'xkTYK6w+vp',
    description:
      'Routine intro chat: closing line for a system preset without setup questions',
  },
});

/** How the workflow runs out of the box, as a conversational sentence. */
function cadenceSentence(intl: IntlShape, workflow: Workflow): string {
  if (workflow.suggested_cadence === 'daily') {
    return intl.formatMessage(INTRO_MESSAGES.cadenceDaily);
  }
  if (workflow.suggested_cadence === 'weekly') {
    return intl.formatMessage(INTRO_MESSAGES.cadenceWeekly);
  }
  if ((workflow.suggested_event_types ?? []).length > 0) {
    return intl.formatMessage(INTRO_MESSAGES.cadenceAfterNote);
  }
  return intl.formatMessage(INTRO_MESSAGES.cadenceOnDemand);
}

/**
 * Whether a workflow is "open-ended": a bespoke, on-demand routine a clinician
 * wrote for themselves — instructed (not recorded), not a fork of a curated
 * preset, with no schedule and no after-note trigger. Its intent is fluid, so
 * the opener stays short. Anything with a clear trigger, a recorded script,
 * or a curated system definition reads as well-defined and gets the fuller
 * breakdown.
 */
export function isOpenEndedWorkflow(workflow: Workflow): boolean {
  const hasSchedule =
    workflow.suggested_cadence === 'daily' || workflow.suggested_cadence === 'weekly';
  const hasTrigger = (workflow.suggested_event_types ?? []).length > 0;
  const isForkOfPreset = Boolean(workflow.forked_from_workflow_id);
  return (
    workflow.creator_type === 'user' &&
    workflow.source === 'instructed' &&
    !isForkOfPreset &&
    !hasSchedule &&
    !hasTrigger
  );
}

/**
 * Conversational breakdown of what the workflow does, in Heidi's voice.
 *
 * Well-defined workflows get the full picture — what it does, what it produces
 * each run, and when it runs. Open-ended ones get a deliberately short opener.
 */
export function buildIntro(
  intl: IntlShape,
  workflow: Workflow,
  options: BuildIntroOptions = {}
): string {
  const triggerLine = options.triggerLine ?? null;
  const neverRunClose = options.neverRun
    ? intl.formatMessage(INTRO_MESSAGES.neverRun, { name: workflow.name })
    : null;

  if (isOpenEndedWorkflow(workflow) && !triggerLine) {
    const lines = [
      intl.formatMessage(INTRO_MESSAGES.openEndedGreeting, { name: workflow.name }),
    ];
    if (workflow.description) lines.push('', workflow.description);
    lines.push('', neverRunClose ?? intl.formatMessage(INTRO_MESSAGES.alreadyYours));
    return lines.join('\n');
  }

  const lines = [
    intl.formatMessage(INTRO_MESSAGES.greeting, { name: workflow.name }),
    '',
    workflow.description,
  ];
  const highlights = workflow.highlights ?? [];
  if (highlights.length > 0) {
    lines.push(
      '',
      intl.formatMessage(INTRO_MESSAGES.eachRunLeadIn),
      '',
      ...highlights.map((h) => `- ${h}`)
    );
  }
  lines.push(
    '',
    triggerLine
      ? ownedTriggerSentence(intl, triggerLine)
      : cadenceSentence(intl, workflow)
  );
  if (workflow.creator_type === 'system') {
    const hasSetupFields = (workflow.setup_fields ?? []).length > 0;
    lines.push(
      '',
      intl.formatMessage(
        hasSetupFields ? INTRO_MESSAGES.makeItYoursWithSetup : INTRO_MESSAGES.makeItYours
      )
    );
  } else {
    lines.push('', neverRunClose ?? intl.formatMessage(INTRO_MESSAGES.alreadyYoursOwned));
  }
  return lines.join('\n');
}

export interface BuildWorkflowContextOptions {
  /** The intro Heidi already "spoke" at the top of the pane. Included in the
   * preamble so the model treats the user's message as a follow-up in that
   * conversation — not the opening line of a fresh chat. */
  openingMessage?: string | null;
}

/**
 * Wire-prompt preamble anchoring the chat to this routine, so the agent's
 * routine tools (update_routine / schedule_routine /
 * set_routine_event_triggers) target it when the user asks for changes in
 * natural language. Prepended to the wire text only — the user's bubble
 * shows their own words via ``displayPrompt``.
 *
 * Workflow fields that reach the wire prompt, directly here or via the
 * ``openingMessage`` that {@link buildIntro} produces: ``name``,
 * ``description``, ``highlights``, ``suggested_cadence``, the ``setup_fields``
 * labels, ``instructions``, and the resolved schedule / event trigger line.
 * These are AI-prompt-eligible by design — none of them may be treated as
 * private configuration, so nothing secret (credentials, internal-only notes)
 * belongs in them.
 */
export function buildWorkflowContext(
  workflow: Workflow,
  options: BuildWorkflowContextOptions = {}
): string {
  // An Explore catalog preset has no workflow row behind it — its id is a
  // template id, so workflow tools called against it fail with "not found".
  // The context must say so, or the agent updates the preset id, errors, and
  // stalls asking the user whether to create the routine (the answer is
  // always yes — asking to change it IS the ask to have it).
  const isCatalogPreset = isPresetCatalogWorkflow(workflow);
  const lines = isCatalogPreset
    ? [
        `Context: the user is exploring "${workflow.name}", a Heidi preset ` +
          `from the routines catalog. It is NOT in their library yet, so it ` +
          `has no usable workflow_id — "${workflow.workflow_id}" is only the ` +
          `catalog preset id, and routine tools will fail against it.`,
        `The preset's instructions are:\n${workflow.instructions}`,
      ]
    : [
        `Context: the user is viewing their routine "${workflow.name}" ` +
          `(workflow_id: ${workflow.workflow_id}).`,
        `Its instructions are:\n${workflow.instructions}`,
      ];
  const opening = options.openingMessage?.trim();
  if (opening) {
    lines.push(
      `You already opened this conversation by introducing the routine with ` +
        `the message below — the user is reading it directly above their reply:\n` +
        `"""\n${opening}\n"""`,
      `Treat their message as a follow-up in that conversation about this ` +
        `routine, not the start of a new chat. Don't re-introduce yourself or ` +
        `greet from scratch; even a bare greeting should be answered in this ` +
        `context (e.g. offering to explain, run, or adjust this routine). ` +
        `Stay anchored to the routine unless they clearly change topic.`
    );
  }
  lines.push(
    isCatalogPreset
      ? `If they ask to change how it works, when it runs, or what triggers ` +
          `it — or they answer setup questions — that IS their request to add ` +
          `it: create the routine in their library from the preset's ` +
          `instructions with their changes applied, then set any schedule or ` +
          `triggers on the NEW routine's id. Do it in one flow — never go ` +
          `looking for an existing workflow_id, and never pause to ask ` +
          `whether they want it created or saved. Then briefly confirm what ` +
          `you set up.`
      : `When they ask to change what it does, when it runs, or what triggers ` +
          `it, apply the change to this routine with the routine tools ` +
          `(update_routine, schedule_routine, set_routine_event_triggers).`
  );
  return `${lines.join('\n')}\n\n${WORKFLOW_CONTEXT_USER_REQUEST_MARKER}`;
}

/**
 * Reveals the overview one paragraph block at a time after an initial
 * delay — each block fades in slowly at the pane, giving the deliberate,
 * well-paced reveal of the Pi discover conversation. Reduced motion shows
 * everything immediately.
 */
export function useStreamedBlocks(text: string): {
  visible: string[];
  /** Every block, regardless of how far the reveal has got — the chat handoff
   * carries the whole intro as its leading turn. */
  all: string[];
  streaming: boolean;
} {
  const reducedMotion = useReducedMotion();
  const blocks = useMemo(
    () => text.split('\n\n').filter((block) => block.trim().length > 0),
    [text]
  );
  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount(0);
    if (reducedMotion) {
      setCount(blocks.length);
      return undefined;
    }
    let interval: ReturnType<typeof setInterval> | undefined;
    const start = setTimeout(() => {
      setCount(1);
      interval = setInterval(() => {
        setCount((n) => {
          const next = n + 1;
          if (next >= blocks.length && interval) clearInterval(interval);
          return next;
        });
      }, BLOCK_INTERVAL_MS);
    }, HERO_DELAY_MS);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [blocks, reducedMotion]);

  return {
    visible: blocks.slice(0, count),
    all: blocks,
    streaming: count < blocks.length,
  };
}
